import axios from "axios";
import apiConfig from "../config/api.js";

/**
 * Service pour la gestion des recettes
 * Classe orientée objet pour encapsuler toutes les opérations API
 */
class RecipeService {
  constructor(baseURL = apiConfig.baseURL) {
    this.baseURL = baseURL;
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Configuration des intercepteurs
    this.setupInterceptors();
  }

  /**
   * Configuration des intercepteurs pour la gestion des erreurs
   */
  setupInterceptors() {
    // Intercepteur de requête
    this.axiosInstance.interceptors.request.use(
      (config) => {
        console.log(
          `🚀 Requête API: ${config.method?.toUpperCase()} ${config.url}`
        );
        return config;
      },
      (error) => {
        console.error("❌ Erreur de requête:", error);
        return Promise.reject(error);
      }
    );

    // Intercepteur de réponse
    this.axiosInstance.interceptors.response.use(
      (response) => {
        console.log(
          `✅ Réponse API: ${response.status} ${response.config.url}`
        );
        return response;
      },
      (error) => {
        console.error(
          "❌ Erreur de réponse:",
          error.response?.data || error.message
        );
        return Promise.reject(this.handleError(error));
      }
    );
  }

  /**
   * Gestion centralisée des erreurs
   * @param {Error} error - Erreur à traiter
   * @returns {Object} Erreur formatée
   */
  handleError(error) {
    if (error.response) {
      // Erreur de réponse du serveur
      return {
        message: error.response.data?.error || "Erreur serveur",
        status: error.response.status,
        data: error.response.data,
      };
    } else if (error.request) {
      // Erreur de réseau
      return {
        message: "Erreur de connexion au serveur",
        status: 0,
        data: null,
      };
    } else {
      // Autre erreur
      return {
        message: error.message || "Erreur inconnue",
        status: 0,
        data: null,
      };
    }
  }

  /**
   * Récupérer toutes les recettes
   * @returns {Promise<Array>} Liste des recettes
   */
  async getAllRecipes() {
    try {
      const response = await this.axiosInstance.get("/recipes");
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Récupérer une recette par ID
   * @param {number} id - ID de la recette
   * @returns {Promise<Object>} Recette
   */
  async getRecipeById(id) {
    try {
      const response = await this.axiosInstance.get(`/recipes/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Créer une nouvelle recette
   * @param {Object} recipeData - Données de la recette
   * @returns {Promise<Object>} Réponse de création
   */
  async createRecipe(recipeData) {
    try {
      const response = await this.axiosInstance.post("/recipes", recipeData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Mettre à jour une recette
   * @param {number} id - ID de la recette
   * @param {Object} recipeData - Nouvelles données
   * @returns {Promise<Object>} Réponse de mise à jour
   */
  async updateRecipe(id, recipeData) {
    try {
      const response = await this.axiosInstance.put(
        `/recipes/${id}`,
        recipeData
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Supprimer une recette
   * @param {number} id - ID de la recette
   * @returns {Promise<Object>} Réponse de suppression
   */
  async deleteRecipe(id) {
    try {
      const response = await this.axiosInstance.delete(`/recipes/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Tester la connexion à l'API
   * @returns {Promise<Object>} Réponse de test
   */
  async testConnection() {
    try {
      const response = await this.axiosInstance.get("/test");
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// Export d'une instance singleton
export default new RecipeService();
